import {
  BK, BM, EMPTY, WK, WM,
  colorOf, isKing, opposite,
} from '../types.js';
import type { Cell, Color, GameStatus, Move, Position, PositionKey, Square } from '../types.js';
import { colOf, rowOf, sqAt, sqToAlg } from '../board.js';

/** Четыре диагональных направления (dcol, drow). */
const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
];

function manOf(c: Color): Cell {
  return c === 'white' ? WM : BM;
}
function kingOf(c: Color): Cell {
  return c === 'white' ? WK : BK;
}
function lastRow(c: Color): number {
  return c === 'white' ? 7 : 0;
}

/** Соседнее тёмное поле в направлении или -1. */
function step(sq: Square, dc: number, dr: number): Square | -1 {
  return sqAt(colOf(sq) + dc, rowOf(sq) + dr);
}

/** Начальная расстановка: белые ряды 1–3, чёрные 6–8, ход белых. */
export function initialPosition(): Position {
  const board: Cell[] = new Array<Cell>(32).fill(EMPTY);
  for (let i = 0; i < 12; i++) board[i] = WM;
  for (let i = 20; i < 32; i++) board[i] = BM;
  return { board, turn: 'white', quiet: 0 };
}

interface CaptureStep {
  victim: Square;
  landings: Square[];
}

/**
 * Возможные одиночные взятия фигурой на `cur`.
 * `board` — доска, где сама фигура снята с исходного поля (она «в руке»);
 * `captured` — уже побитые в этой цепочке поля: остаются препятствием (турецкий удар),
 * бить их второй раз нельзя.
 */
function captureSteps(
  board: Cell[], cur: Square, king: boolean, color: Color, captured: Square[],
): CaptureStep[] {
  const out: CaptureStep[] = [];
  for (const [dc, dr] of DIRS) {
    if (!king) {
      const victim = step(cur, dc, dr);
      if (victim < 0) continue;
      const vCell = board[victim] as Cell;
      if (colorOf(vCell) !== opposite(color)) continue;
      if (captured.includes(victim)) continue;
      const landing = step(victim, dc, dr);
      if (landing < 0 || board[landing] !== EMPTY) continue;
      out.push({ victim, landings: [landing] });
    } else {
      // дамка: скользим по пустым до первой фигуры
      let s = step(cur, dc, dr);
      while (s >= 0 && board[s] === EMPTY) s = step(s, dc, dr);
      if (s < 0) continue;
      const vCell = board[s] as Cell;
      if (colorOf(vCell) !== opposite(color)) continue; // своя — стоп
      if (captured.includes(s)) continue; // уже побита — препятствие
      const landings: Square[] = [];
      let l = step(s, dc, dr);
      while (l >= 0 && board[l] === EMPTY) {
        landings.push(l);
        l = step(l, dc, dr);
      }
      if (landings.length > 0) out.push({ victim: s, landings });
    }
  }
  return out;
}

/** DFS по цепочкам боя. Записывает только завершённые цепочки (продолжений нет). */
function dfsCaptures(
  board: Cell[], from: Square, cur: Square, king: boolean, startedAsKing: boolean,
  color: Color, path: Square[], captured: Square[], out: Move[],
): void {
  const steps = captureSteps(board, cur, king, color, captured);
  if (steps.length === 0) {
    if (path.length > 0) {
      out.push({
        from,
        path: path.slice(),
        captured: captured.slice(),
        promoted: !startedAsKing && king,
      });
    }
    return;
  }
  for (const { victim, landings } of steps) {
    for (const landing of landings) {
      // превращение в бою: простая, вставшая на последнюю горизонталь,
      // немедленно продолжает бой дамкой
      const nextKing = king || rowOf(landing) === lastRow(color);
      path.push(landing);
      captured.push(victim);
      dfsCaptures(board, from, landing, nextKing, startedAsKing, color, path, captured, out);
      path.pop();
      captured.pop();
    }
  }
}

/** Все ходы-взятия стороны `pos.turn`. */
function captureMoves(pos: Position): Move[] {
  const out: Move[] = [];
  for (let sq = 0; sq < 32; sq++) {
    const cell = pos.board[sq] as Cell;
    if (colorOf(cell) !== pos.turn) continue;
    const king = isKing(cell);
    const board = pos.board.slice();
    board[sq] = EMPTY; // фигура «в руке»
    dfsCaptures(board, sq, sq, king, king, pos.turn, [], [], out);
  }
  return out;
}

/** Тихие ходы стороны `pos.turn` (без учёта обязательности боя). */
function quietMoves(pos: Position): Move[] {
  const out: Move[] = [];
  const fwd = pos.turn === 'white' ? 1 : -1;
  for (let sq = 0; sq < 32; sq++) {
    const cell = pos.board[sq] as Cell;
    if (colorOf(cell) !== pos.turn) continue;
    if (!isKing(cell)) {
      for (const dc of [-1, 1]) {
        const to = step(sq, dc, fwd);
        if (to >= 0 && pos.board[to] === EMPTY) {
          out.push({
            from: sq, path: [to], captured: [],
            promoted: rowOf(to) === lastRow(pos.turn),
          });
        }
      }
    } else {
      for (const [dc, dr] of DIRS) {
        let to = step(sq, dc, dr);
        while (to >= 0 && pos.board[to] === EMPTY) {
          out.push({ from: sq, path: [to], captured: [], promoted: false });
          to = step(to, dc, dr);
        }
      }
    }
  }
  return out;
}

/**
 * Полный список допустимых ходов. Бой обязателен: если есть хоть одно взятие,
 * тихих ходов в списке нет. Правила большинства нет — все варианты боя равноправны.
 */
export function legalMoves(pos: Position): Move[] {
  const caps = captureMoves(pos);
  return caps.length > 0 ? caps : quietMoves(pos);
}

/** Применить ход (предполагается, что он из legalMoves). Не мутирует вход. */
export function applyMove(pos: Position, move: Move): Position {
  const board = pos.board.slice();
  const piece = board[move.from] as Cell;
  const color = colorOf(piece);
  if (color === null) throw new Error('applyMove: empty from-square');
  const wasKing = isKing(piece);
  board[move.from] = EMPTY;
  for (const c of move.captured) board[c] = EMPTY; // снимаются после завершения хода
  const dest = move.path[move.path.length - 1];
  if (dest === undefined) throw new Error('applyMove: empty path');
  const finalKing = wasKing || move.promoted;
  board[dest] = finalKing ? kingOf(color) : manOf(color);
  const quiet = move.captured.length === 0 && wasKing ? pos.quiet + 1 : 0;
  return { board, turn: opposite(pos.turn), quiet };
}

/** Ключ позиции: расстановка + чей ход (счётчики не входят). */
export function positionKey(pos: Position): PositionKey {
  return pos.board.join('') + '|' + pos.turn;
}

/**
 * Статус партии. `history` — ключи всех встречавшихся позиций, включая текущую
 * (сервер добавляет ключ после каждого хода и ключ начальной позиции).
 * Ничья «15 ходов дамками» = 30 полуходов подряд только дамками без взятий (pos.quiet >= 30).
 */
export function gameStatus(pos: Position, history: PositionKey[]): GameStatus {
  let has = false;
  for (const cell of pos.board) {
    if (colorOf(cell as Cell) === pos.turn) { has = true; break; }
  }
  if (!has) return { state: 'win', winner: opposite(pos.turn), reason: 'no-pieces' };
  if (legalMoves(pos).length === 0) {
    return { state: 'win', winner: opposite(pos.turn), reason: 'no-moves' };
  }
  if (pos.quiet >= 30) return { state: 'draw', reason: 'kings-15' };
  const key = positionKey(pos);
  let n = 0;
  for (const k of history) if (k === key) n++;
  if (n >= 3) return { state: 'draw', reason: 'repetition' };
  return { state: 'playing' };
}

/** Русская нотация: тире — тихий ход, двоеточия — взятие (c3:e5:g7). */
export function moveNotation(move: Move): string {
  const sep = move.captured.length > 0 ? ':' : '-';
  return sqToAlg(move.from) + sep + move.path.map(sqToAlg).join(':');
}

/** Ходы совпадают по значению (from, path, captured). */
export function sameMove(a: Move, b: Move): boolean {
  return a.from === b.from
    && a.path.length === b.path.length && a.path.every((s, i) => s === b.path[i])
    && a.captured.length === b.captured.length
    && a.captured.every((s, i) => s === b.captured[i]);
}
